import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { filter, Subject, takeUntil } from 'rxjs';
import { BehaviourTrackerService } from '../../service/behaviour-tracker.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { UpdateDailyScoreRequest } from '../../core/behavior-tracker.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { dailyScoreInfo } from '../../core/behavior-tracker.selectors';
import {
  profile,
  selectedUserProfile,
} from '../../../../shared/core/shared.selectors';
import { roles } from '../../../../shared/constants/common.constants';
import { CommonService } from '../../../../shared/services/common.service';

@Component({
  selector: 'app-daily-score-panel',
  templateUrl: './daily-score-panel.component.html',
  styleUrls: ['./daily-score-panel.component.css'],
})
export class DailyScorePanelComponent implements OnInit, OnDestroy {
  @Input() showDetailsForAdult = false;
  lastSession: number;
  date: string = '';
  form = new FormGroup<any>({});
  dailyScoreUpdateLoader: boolean = false;
  weekNumber: number;
  groupId: number;
  selectedUserGroupId: number;
  isDrillDown: boolean = false;
  memberId: number;
  private readonly onDestroy: Subject<any> = new Subject<any>();
  data: any;

  constructor(
    private participantService: BehaviourTrackerService,
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private commonService: CommonService,
    private router: Router,
  ) {
    const params: any = this.route.snapshot.params;
    this.isDrillDown = !!(
      (params && params?.clientAdminId) ||
      params?.coachId ||
      params?.memberId
    );
    this.memberId = params && params?.memberId;
  }

  ngOnInit() {
    this.weekNumber = Number(this.route.snapshot.queryParams['weekNumber']);

    this.store
      .select(profile)
      .pipe(
        takeUntil(this.onDestroy),
        filter((data) => !!data),
      )
      .subscribe((data) => {
        this.groupId = data.role === roles['adult'] ? -1 : data?.groupId;
      });

    this.store
      .select(selectedUserProfile)
      .pipe(
        takeUntil(this.onDestroy),
        filter((data) => !!data),
      )
      .subscribe((data) => {
        this.selectedUserGroupId =
          data?.profile?.role === roles['adult'] ? -1 : data?.profile?.groupId;
      });

    this.participantService.selectedDayData
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.form.reset();
        this.data = data;
        this.date = data['date'];
        this.lastSession = data['lastSession'];

        if (this.showDetailsForAdult || this.lastSession >= 2) {
          this.form.addControl(
            'redLight',
            new FormControl({ value: null, disabled: false }),
          );
        }

        if (this.showDetailsForAdult || this.lastSession >= 8) {
          this.form.addControl(
            'physicalActivity',
            new FormControl({ value: null, disabled: false }),
          );
        }

        if (this.showDetailsForAdult || this.lastSession >= 15) {
          this.form.addControl(
            'hours',
            new FormControl({ value: null, disabled: false }, [
              Validators.max(24),
            ]),
          );
          this.form.addControl(
            'minutes',
            new FormControl({ value: null, disabled: false }, [
              Validators.max(59),
            ]),
          );
        }

        const time = this.convertMinutesToHoursAndMinutes(data['stPoints']);

        this.form.patchValue(
          {
            redLight:
              data?.isRlImputed && data['fiPoints'] === null
                ? null
                : data['rlValue'],
            physicalActivity: data['paPoints'],
            hours: data.isStImputed ? null : time?.hours,
            minutes: data.isStImputed ? null : time?.minutes,
          },
          { emitEvent: false },
        );
      });

    this.store
      .select(dailyScoreInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.dailyScoreUpdateLoader = data.loading;
      });
  }

  redirectToJournal() {
    const urlPrefix = this.router.url.split('/')[1];
    let url = `/${urlPrefix}/my-journal`;
    if (this.isDrillDown && this.memberId) {
      url = `/${urlPrefix}/member/${this.memberId}/my-journal`;
    }
    this.router.navigate([url], {
      queryParams: {
        weekNumber: this.weekNumber,
        date: this.date,
      },
    });
  }

  save() {
    // Red Light Foods validation
    const redLight = this.form.get('redLight')?.value;
    if (
      this.form.contains('redLight') &&
      this.form.get('redLight')?.dirty &&
      (redLight === '' ||
        redLight === null ||
        isNaN(redLight) ||
        redLight < 0 ||
        redLight > 99)
    ) {
      this.commonService.notification(
        'Please enter a daily red light food value between 0 and 99.',
        'danger',
        '',
        1000,
      );
      return;
    }

    // Physical Activity validation
    const physicalActivity = this.form.get('physicalActivity')?.value;
    if (
      this.form.contains('physicalActivity') &&
      this.form.get('physicalActivity')?.dirty &&
      (physicalActivity === '' ||
        physicalActivity === null ||
        isNaN(physicalActivity) ||
        !(
          Number(physicalActivity) === 0 ||
          (Number(physicalActivity) >= 100 && Number(physicalActivity) <= 30000)
        ))
    ) {
      this.commonService.notification(
        'Please enter a daily physical activity value of 0 or between 100 and 30,000.',
        'danger',
        '',
        1000,
      );
      return;
    }

    if (this.form.invalid) {
      return;
    }

    let message = '';

    if (
      this.data?.eJournalRLExists &&
      this.form?.controls?.['redLight']?.dirty
    ) {
      message = '<span class="text-[rgb(224,79,57)]">Red Light</span>';
    }

    if (
      this.data?.eJournalPAExists &&
      this.form?.controls?.['physicalActivity']?.dirty
    ) {
      if (message.length) {
        message += ' / ';
      }
      message +=
        '<span class="text-[rgb(89,179,169)]">Physical Activity</span>';
    }

    if (
      this.data?.eJournalSTExists &&
      (this.form?.controls?.['hours']?.dirty ||
        this.form?.controls?.['minutes']?.dirty)
    ) {
      if (message.length) {
        message += ' / ';
      }
      message += '<span class="text-[rgb(0,112,224)]">Screen Time</span>';
    }

    if (message) {
      this.commonService.openConfirmationDialog({
        data: {
          message: `You have ${message} e-Journal entries for this date. Do you want to delete your e-Journal entries and replace with a total daily value in your Behavior Tracker?`,
          submitText: 'Yes',
          classList: 'w-[40vw]',
          handleSubmit: () => {
            this.commonService.closeDialog();
            this.commitChanges();
          },
          cancelText: 'No',
          handleCancel: () => {
            this.commonService.closeDialog();
            this.resetForm();
          },
        },
      });
      return;
    }

    this.commitChanges();
  }

  resetForm() {
    const time = this.convertMinutesToHoursAndMinutes(this.data['stPoints']);
    this.form.patchValue(
      {
        redLight:
          this.data?.isRlImputed && this.data['fiPoints'] === null
            ? null
            : this.data['rlValue'],
        physicalActivity: this.data['paPoints'],
        hours: this.data.isStImputed ? null : time?.hours,
        minutes: this.data.isStImputed ? null : time?.minutes,
      },
      { emitEvent: false },
    );
    this.form.markAsUntouched();
    this.form.markAsPristine();
  }

  commitChanges() {
    const { redLight, physicalActivity, hours, minutes } =
      this.form.getRawValue();

    let m: number;

    if (
      (hours === null || hours === '') &&
      (minutes === null || minutes === '')
    ) {
      m = null;
    } else {
      if (hours !== undefined && hours !== null && hours !== '') {
        m = Number(hours) * 60;
      }

      if (minutes !== undefined && minutes !== null && minutes !== '') {
        if (m) {
          m += Number(minutes);
        } else {
          m = Number(minutes);
        }
      }
    }

    const payload = {
      date: this.date,
      weekNumber: this.weekNumber,
      groupId: this.groupId,
      ...((this.showDetailsForAdult || this.lastSession >= 2) &&
      !isNaN(redLight) &&
      this.form?.controls?.['redLight']?.dirty
        ? {
            redLight:
              redLight !== undefined && redLight !== null && redLight !== ''
                ? Number(redLight)
                : null,
          }
        : {}),
      ...((this.showDetailsForAdult || this.lastSession >= 8) &&
      !isNaN(physicalActivity) &&
      this.form?.controls?.['physicalActivity']?.dirty
        ? {
            physicalActivity:
              physicalActivity !== undefined &&
              physicalActivity !== null &&
              physicalActivity !== ''
                ? Number(physicalActivity)
                : null,
          }
        : {}),
      ...((this.showDetailsForAdult || this.lastSession >= 15) &&
      m !== undefined &&
      (this.form?.controls?.['hours']?.dirty ||
        this.form?.controls?.['minutes']?.dirty)
        ? { screenTime: m }
        : {}),
      ...(this.route.snapshot.params['memberId']
        ? { forUserId: this.route.snapshot.params['memberId'] }
        : {}),
      ...(this.route.snapshot.params['memberId']
        ? { groupId: this.selectedUserGroupId }
        : { groupId: this.groupId }),
    };

    this.store.dispatch(UpdateDailyScoreRequest({ payload }));
  }

  convertMinutesToHoursAndMinutes(totalMinutes) {
    if (totalMinutes === null) {
      return { hours: null, minutes: null };
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes };
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
